import { useCallback, useRef, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useApi } from './useApi';
import ErrorHandlingService from '../services/ErrorHandlingService';

/**
 * Enhanced Error Handling Hook
 * Provides comprehensive error handling with retry mechanisms and user feedback
 */
export const useErrorHandling = () => {
  const { showSuccess, showError, showWarning, showInfo, showToast } = useToast();
  const { apiClient } = useApi();
  const errorServiceRef = useRef(null);

  // Initialize error handling service
  useEffect(() => {
    if (!errorServiceRef.current) {
      errorServiceRef.current = new ErrorHandlingService(
        { showSuccess, showError, showWarning, showInfo, showToast },
        apiClient
      );
    }
  }, [showSuccess, showError, showWarning, showInfo, showToast, apiClient]);

  /**
   * Handle API errors with comprehensive error processing
   */
  const handleApiError = useCallback(async (error, context = {}, options = {}) => {
    if (!errorServiceRef.current) return null;
    return await errorServiceRef.current.handleApiError(error, context, options);
  }, []);

  /**
   * Handle file operation errors
   */
  const handleFileError = useCallback(async (error, fileInfo = {}, operation = 'file_operation') => {
    if (!errorServiceRef.current) return null;
    return await errorServiceRef.current.handleFileError(error, fileInfo, operation);
  }, []);

  /**
   * Handle parrot assignment errors
   */
  const handleAssignmentError = useCallback(async (error, assignmentInfo = {}) => {
    if (!errorServiceRef.current) return null;
    return await errorServiceRef.current.handleAssignmentError(error, assignmentInfo);
  }, []);

  /**
   * Execute operation with comprehensive error handling
   */
  const executeWithErrorHandling = useCallback(async (
    operation,
    operationFn,
    options = {}
  ) => {
    const {
      context = {},
      showSuccessMessage = true,
      successMessage = 'Operation completed successfully',
      retryable = true,
      maxRetries = 3
    } = options;

    try {
      const result = await operationFn();
      
      if (showSuccessMessage) {
        showSuccess(successMessage);
      }
      
      // Clear any retry attempts for this operation
      if (errorServiceRef.current) {
        errorServiceRef.current.clearRetryAttempts(operation);
      }
      
      return result;
    } catch (error) {
      return await handleApiError(error, context, {
        operation,
        allowRetry: retryable,
        onRetry: retryable ? operationFn : null
      });
    }
  }, [handleApiError, showSuccess]);

  /**
   * Handle file upload with progress and error handling
   */
  const handleFileUpload = useCallback(async (
    files,
    uploadFn,
    options = {}
  ) => {
    const {
      onProgress = null,
      onFileComplete = null,
      onAllComplete = null,
      showIndividualSuccess = true
    } = options;

    const results = [];
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        if (onProgress) {
          onProgress(i, files.length, file.name);
        }

        const result = await uploadFn(file);
        results.push({ file, result, success: true });
        successCount++;

        if (showIndividualSuccess && files.length === 1) {
          showSuccess(`File "${file.name}" uploaded successfully`);
        }

        if (onFileComplete) {
          onFileComplete(file, result, true);
        }

      } catch (error) {
        const errorInfo = await handleFileError(error, {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        }, 'upload');

        results.push({ file, error: errorInfo, success: false });
        errorCount++;

        if (onFileComplete) {
          onFileComplete(file, errorInfo, false);
        }
      }
    }

    // Show summary for multiple files
    if (files.length > 1) {
      if (successCount > 0 && errorCount === 0) {
        showSuccess(`Successfully uploaded ${successCount} file${successCount !== 1 ? 's' : ''}`);
      } else if (successCount > 0 && errorCount > 0) {
        showWarning(`Uploaded ${successCount} file${successCount !== 1 ? 's' : ''}, ${errorCount} failed`);
      } else if (errorCount > 0) {
        showError(`Failed to upload ${errorCount} file${errorCount !== 1 ? 's' : ''}`);
      }
    }

    if (onAllComplete) {
      onAllComplete(results, successCount, errorCount);
    }

    return results;
  }, [handleFileError, showSuccess, showWarning, showError]);

  /**
   * Handle file deletion with confirmation and error handling
   */
  const handleFileDelete = useCallback(async (
    file,
    deleteFn,
    options = {}
  ) => {
    const {
      showConfirmation = true,
      confirmationMessage = null,
      onSuccess = null,
      onError = null
    } = options;

    try {
      // Show confirmation if requested
      if (showConfirmation) {
        const confirmed = window.confirm(
          confirmationMessage || `Are you sure you want to delete "${file.name}"? This action cannot be undone.`
        );
        
        if (!confirmed) {
          return { cancelled: true };
        }
      }

      const result = await deleteFn(file);
      
      showSuccess(`File "${file.name}" deleted successfully`);
      
      if (onSuccess) {
        onSuccess(file, result);
      }
      
      return { success: true, result };

    } catch (error) {
      const errorInfo = await handleFileError(error, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      }, 'delete');

      if (onError) {
        onError(file, errorInfo);
      }

      return { success: false, error: errorInfo };
    }
  }, [handleFileError, showSuccess]);

  /**
   * Handle parrot file assignment with error handling
   */
  const handleParrotAssignment = useCallback(async (
    file,
    parrot,
    assignFn,
    operation = 'assign'
  ) => {
    try {
      const result = await assignFn(file, parrot);
      
      const actionText = operation === 'assign' ? 'assigned to' : 'removed from';
      showSuccess(`File "${file.name}" ${actionText} "${parrot.name}" successfully`);
      
      return { success: true, result };

    } catch (error) {
      const errorInfo = await handleAssignmentError(error, {
        fileName: file.name,
        parrotName: parrot.name,
        operation
      });

      return { success: false, error: errorInfo };
    }
  }, [handleAssignmentError, showSuccess]);

  /**
   * Create a retry function for failed operations
   */
  const createRetryFunction = useCallback((
    originalFunction,
    operation,
    context = {}
  ) => {
    return async (...args) => {
      try {
        return await originalFunction(...args);
      } catch (error) {
        return await handleApiError(error, context, {
          operation,
          allowRetry: true,
          onRetry: () => originalFunction(...args)
        });
      }
    };
  }, [handleApiError]);

  /**
   * Show user-friendly error message
   */
  const showUserFriendlyError = useCallback((errorCode, context = {}) => {
    if (!errorServiceRef.current) return;
    
    const message = errorServiceRef.current.getUserFriendlyMessage(errorCode, context);
    showError(message);
  }, [showError]);

  /**
   * Clear retry attempts for an operation
   */
  const clearRetryAttempts = useCallback((operation) => {
    if (errorServiceRef.current) {
      errorServiceRef.current.clearRetryAttempts(operation);
    }
  }, []);

  return {
    handleApiError,
    handleFileError,
    handleAssignmentError,
    executeWithErrorHandling,
    handleFileUpload,
    handleFileDelete,
    handleParrotAssignment,
    createRetryFunction,
    showUserFriendlyError,
    clearRetryAttempts
  };
};

/**
 * Hook for handling specific Knowledge Base operations
 */
export const useKnowledgeBaseErrorHandling = () => {
  const errorHandling = useErrorHandling();
  const { execute } = useApi();

  const handleKBFileDelete = useCallback(async (file) => {
    return await errorHandling.executeWithErrorHandling(
      `delete_kb_file_${file.id}`,
      async () => {
        return await execute(async (api) => {
          const result = await api.delete(`/api/knowledge-base/${file.id}`);
          
          // Invalidate cache after successful deletion
          api.invalidateCache('/api/knowledge-base');
          api.invalidateCache('/api/knowledge-base/files');
          
          return result;
        });
      },
      {
        context: {
          fileName: file.name,
          fileId: file.id,
          operation: 'delete'
        },
        successMessage: `File "${file.name}" deleted successfully`,
        retryable: true
      }
    );
  }, [errorHandling, execute]);

  const handleKBFileUpload = useCallback(async (files) => {
    return await errorHandling.handleFileUpload(
      files,
      async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        return await execute(async (api) => {
          const result = await api.post('/api/knowledge-base', formData);
          
          // Invalidate cache after successful upload
          api.invalidateCache('/api/knowledge-base');
          api.invalidateCache('/api/knowledge-base/files');
          
          return result;
        });
      },
      {
        showIndividualSuccess: files.length === 1
      }
    );
  }, [errorHandling, execute]);

  const handleKBFileAssignment = useCallback(async (file, parrot, operation = 'assign') => {
    return await errorHandling.handleParrotAssignment(
      file,
      parrot,
      async (f, p) => {
        const endpoint = operation === 'assign' 
          ? `/api/parrots/${p.id}/knowledge-base/${f.id}/assign`
          : `/api/parrots/${p.id}/knowledge-base/${f.id}/unassign`;
          
        return await execute(async (api) => {
          return await api.post(endpoint);
        });
      },
      operation
    );
  }, [errorHandling, execute]);

  return {
    ...errorHandling,
    handleKBFileDelete,
    handleKBFileUpload,
    handleKBFileAssignment
  };
};

export default useErrorHandling;
